import * as amplitude from "@amplitude/analytics-browser";
import { ClickAwayListener } from "@mui/base";
import { useRequiredContext } from "@redotech/react-util/context";
import { LoadState } from "@redotech/react-util/load";
import ArrowUpRightIcon from "@redotech/redo-icons/arrow-up-right_filled.svg";
import Send01Svg from "@redotech/redo-icons/send-01.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ExpandedConversation } from "@redotech/redo-model/conversation";
import { Macro } from "@redotech/redo-model/macro";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { allowSupportAiCopilot, SendType } from "@redotech/redo-model/team";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonDropdown } from "@redotech/redo-web/arbiter-components/buttons/redo-dropdown-button";
import {
  RedoList,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import CircleSpinner from "@redotech/redo-web/circle-spinner.svg";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { Flex } from "@redotech/redo-web/flex";
import LightningIcon from "@redotech/redo-web/icon-old/lightning.svg";
import StarsIcon from "@redotech/redo-web/icon-old/stars-01.svg";
import { Text } from "@redotech/redo-web/text";
import { OverflowTooltip } from "@redotech/redo-web/tooltip/overflow-tooltip";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { assertNever } from "@redotech/util/type";
import { browserIsMac } from "@redotech/web-util/browser";
import * as React from "react";
import { KeyboardEvent, memo, SetStateAction, useMemo, useState } from "react";
import { AutocompleteType } from "../support-message-autocomplete";
import {
  QuillOperation,
  WritingAiAssistantMenu,
} from "../writing-ai-assistant-menu";
import { hotkeyTooltip } from "./hotkeys";
import { getSentTypeDisplayText } from "./message-input";
import * as messageInputCss from "./message-input.module.css";

interface MessageInputFooterProps {
  macrosLoad: LoadState<Macro[] | undefined>;
  conversation: ExpandedConversation;
  setMacroModalOpen: (open: boolean) => void;
  handleAiAction: (content: string, operation: QuillOperation) => void;
  deleteLoading: boolean;
  saveDraftLoading: boolean;
  handleDeleteDraft: () => Promise<void>;
  replyPending: boolean;
  replyAndClosePending: boolean;
  conversationClosing: boolean | undefined;
  handleSendAndClose: (
    event: React.ChangeEvent<{ value?: unknown }>,
  ) => Promise<void>;
  handleMessageSend: (
    event: React.ChangeEvent<{ value?: unknown }>,
    alertTitle?: string,
    preventUpdateActiveConversation?: boolean,
    markInProgress?: boolean,
  ) => Promise<boolean>;
  sendMessageDisabled: boolean;
  draftId?: string;
  onHotkeyFunctionReady: (
    hotkeyFunction: (event: KeyboardEvent) => void,
  ) => void;
  disableEditor: boolean;
  editorErrorMessage: { message: string; link?: string } | undefined;
  messageRespondingToId: string;
  shouldShowAiGeneratedResponseBadge: boolean;
}

export const MessageInputFooter = memo(function MessageInputFooter({
  macrosLoad,
  conversation,
  setMacroModalOpen,
  handleAiAction,
  deleteLoading,
  saveDraftLoading,
  handleDeleteDraft,
  replyPending,
  replyAndClosePending,
  conversationClosing,
  handleSendAndClose,
  handleMessageSend,
  sendMessageDisabled,
  draftId,
  onHotkeyFunctionReady,
  disableEditor,
  editorErrorMessage,
  messageRespondingToId,
  shouldShowAiGeneratedResponseBadge,
}: MessageInputFooterProps) {
  const team = useRequiredContext(TeamContext);
  const canUseMacros = useHasPermission(SupportPermissions.USE_MACRO);
  const isMac = browserIsMac();

  const [emailSendButtonRef, setEmailSendButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const [focusedIndex, setFocusedIndex] = useState<number | undefined>();
  const [pickingSendType, setPickingSendType] = useState(false);
  const [sendTooltipOpen, setSendTooltipOpen] = useState(false);

  const [sendType, setSendType] = useState<SendType>(
    team.settings.support?.defaultSendAction && conversation.status === "open"
      ? team.settings.support?.defaultSendAction
      : SendType.SEND,
  );

  const handleSetSendType = (state: SetStateAction<SendType>) => {
    setPickingSendType((t) => false);
    setSendType(state);
  };

  async function handleMessageSubmit(
    event: React.ChangeEvent<{ value?: unknown }>,
  ) {
    setPickingSendType(false);
    if (sendType === SendType.CLOSE) {
      await handleSendAndClose(event);
    } else {
      await handleMessageSend(
        event,
        "Message sent",
        false,
        sendType === SendType.IN_PROGRESS,
      );
    }
  }

  const sendDropdownOptions = useMemo(() => {
    const items: RedoListItem<SendType>[] = [
      {
        id: SendType.SEND,
        type: "text",
        value: SendType.SEND,
        text: getSentTypeDisplayText(SendType.SEND),
        hotkey: getHotkeyTooltip(SendType.SEND, isMac, true).split("+"),
      },
      {
        id: SendType.CLOSE,
        type: "text",
        value: SendType.CLOSE,
        text: getSentTypeDisplayText(SendType.CLOSE),
        hotkey: getHotkeyTooltip(SendType.CLOSE, isMac, true).split("+"),
      },
    ];

    if (team.settings.support?.useInProgressStatus) {
      items.push({
        id: SendType.IN_PROGRESS,
        type: "text",
        value: SendType.IN_PROGRESS,
        text: getSentTypeDisplayText(SendType.IN_PROGRESS),
        hotkey: getHotkeyTooltip(SendType.IN_PROGRESS, isMac, true).split("+"),
      });
    }

    return items;
  }, [team, isMac]);

  const canUseAi = allowSupportAiCopilot(team);

  const [editorErrorRef, setEditorErrorRef] = useState<HTMLElement | null>(
    null,
  );

  return (
    <Flex
      align="center"
      className={messageInputCss.wrappingText}
      flex="1 1 0%"
      justify="space-between"
      p="sm"
      pb="lg"
      px="lg"
    >
      <Flex
        align="center"
        className={messageInputCss.wrappingtext}
        flex="1 1 0%"
        gap="xs"
      >
        {!disableEditor && (
          <>
            <WritingAiAssistantMenu
              conversationId={conversation._id}
              draftId={draftId}
              messageRespondingToId={messageRespondingToId}
              onAiOperationComplete={handleAiAction}
              onHotkeyFunctionReady={onHotkeyFunctionReady}
            />
            {canUseAi && canUseMacros && (
              <Flex className={messageInputCss.verticalButtonDivider} />
            )}
            {canUseMacros && (
              <Tooltip
                arrow
                title={getHotkeyTooltip(AutocompleteType.MACRO, isMac)}
              >
                {/** Wrap with span for tooltip events to work https://v4.mui.com/components/tooltips/#disabled-elements */}
                <span>
                  <RedoButton
                    disabled={macrosLoad.pending}
                    hierarchy="tertiary"
                    IconLeading={LightningIcon}
                    onClick={() => {
                      amplitude.logEvent("view-macroModal", {
                        conversationId: conversation._id,
                        channel: conversation.platform,
                      });
                      setMacroModalOpen(true);
                    }}
                    size="md"
                  />
                </span>
              </Tooltip>
            )}
          </>
        )}
      </Flex>
      <Flex align="flex-end" overflow="hidden">
        {shouldShowAiGeneratedResponseBadge && (
          <Flex align="center" gap="xs" pb="md">
            <RedoBadge
              color="orange"
              segmentLeading={{ type: "icon", Icon: () => <StarsIcon /> }}
              size="sm"
              text="AI generated response"
            />
          </Flex>
        )}
        <Flex align="center" overflow="hidden">
          {editorErrorMessage && (
            <>
              {editorErrorMessage.link ? (
                <RedoButton
                  hierarchy="tertiary"
                  IconTrailing={ArrowUpRightIcon}
                  onClick={() =>
                    editorErrorMessage.link &&
                    window.open(editorErrorMessage.link, "_blank")
                  }
                  ref={setEditorErrorRef}
                  text={editorErrorMessage.message}
                  theme="destructive"
                />
              ) : (
                <OverflowTooltip
                  overflowRef={editorErrorRef}
                  tooltipProps={{ title: editorErrorMessage.message }}
                  wrapperClass={messageInputCss.editorErrorTooltipWrapper}
                >
                  <Text
                    overflow="hidden"
                    ref={setEditorErrorRef}
                    textColor="error"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {editorErrorMessage.message}
                  </Text>
                </OverflowTooltip>
              )}
            </>
          )}
          {!disableEditor && (
            <DeleteDraftButton
              deleteLoading={deleteLoading}
              handleDeleteDraft={handleDeleteDraft}
              saveDraftLoading={saveDraftLoading}
            />
          )}
          {!disableEditor && (
            <ClickAwayListener onClickAway={() => setPickingSendType(false)}>
              <span>
                <Tooltip
                  arrow
                  open={sendTooltipOpen && !pickingSendType}
                  title={getHotkeyTooltip(sendType, isMac)}
                >
                  <span
                    onMouseEnter={() => setSendTooltipOpen(true)}
                    onMouseLeave={() => setSendTooltipOpen(false)}
                  >
                    <RedoButtonDropdown
                      disabled={sendMessageDisabled}
                      dropdownOpen={pickingSendType}
                      IconLeading={
                        replyPending ||
                        replyAndClosePending ||
                        conversationClosing
                          ? CircleSpinner
                          : Send01Svg
                      }
                      onClick={handleMessageSubmit}
                      ref={setEmailSendButtonRef}
                      setDropdownOpen={() => {
                        setPickingSendType(
                          (pickingEmailSendType) => !pickingEmailSendType,
                        );
                      }}
                      size="md"
                      text={
                        replyPending || replyAndClosePending
                          ? "Sending..."
                          : conversationClosing
                            ? "Closing..."
                            : getSentTypeDisplayText(sendType)
                      }
                    >
                      <Dropdown
                        anchor={emailSendButtonRef}
                        fitToAnchor={false}
                        open={pickingSendType}
                      >
                        <RedoList
                          focusedIndex={focusedIndex}
                          items={sendDropdownOptions}
                          itemSelected={({ item }) => {
                            handleSetSendType(item.value);
                          }}
                          refToListenTo={null}
                          setFocusedIndex={setFocusedIndex}
                          size="sm"
                        />
                      </Dropdown>
                    </RedoButtonDropdown>
                  </span>
                </Tooltip>
              </span>
            </ClickAwayListener>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const DeleteDraftButton = memo(function DeleteDraftButton({
  deleteLoading,
  saveDraftLoading,
  handleDeleteDraft,
}: {
  deleteLoading: boolean;
  saveDraftLoading: boolean;
  handleDeleteDraft: () => Promise<void>;
}) {
  return (
    <Tooltip arrow title="Discard draft">
      <span>
        <RedoButton
          disabled={deleteLoading || saveDraftLoading}
          hierarchy="tertiary"
          IconLeading={deleteLoading ? CircleSpinner : TrashIcon}
          onClick={() => {
            void handleDeleteDraft();
          }}
          size="md"
        />
      </span>
    </Tooltip>
  );
});

export function getHotkeyTooltip(
  type: AutocompleteType | SendType,
  isMac: boolean,
  trimmed?: boolean,
): string {
  switch (type) {
    case AutocompleteType.DISCOUNT_CODE:
      return hotkeyTooltip({
        action: "Insert",
        code: "5",
        name: "discount code",
        isMac,
        trimmed,
      });
    case AutocompleteType.MACRO:
      return hotkeyTooltip({
        action: "Insert",
        code: "6",
        name: "template",
        isMac,
        trimmed,
      });
    case AutocompleteType.PRODUCT:
      return hotkeyTooltip({
        action: "Insert",
        code: "7",
        name: "product",
        isMac,
        trimmed,
      });
    case AutocompleteType.MENTION:
      return "Insert mention (@)";
    case SendType.SEND:
      return hotkeyTooltip({
        action: undefined,
        code: "Enter",
        name: "Send reply",
        isMac,
        trimmed,
      });
    case SendType.CLOSE:
      return hotkeyTooltip({
        action: undefined,
        code: "⇧+Enter",
        name: "Send reply and close",
        isMac,
        trimmed,
      });
    case SendType.IN_PROGRESS:
      return hotkeyTooltip({
        action: undefined,
        code: `${isMac ? "⌥" : "Alt"}+⇧+Enter`,
        name: "Send reply and mark in progress",
        isMac,
        trimmed,
        useModifierKey: false,
      });
    default:
      return assertNever(type);
  }
}
